<template>
  <vc-date-picker class="inline-block" ref="datePicker" v-model="date" :masks="masks" :disabled="readonly" :min-date="minDate" :max-date="maxDate" :update-on-input="true" @dayclick="updateDate">
    <template v-slot="{ inputValue, togglePopover, showPopover, inputEvents }">
      <div class="flex items-center relative">
        <vs-input
          class="vsinput w-full"
          v-model="inputValue"
          :readonly="readonly"
          @click="onInputClick(readonly, showPopover)"
          :placeholder="placeholder"
          :class="readonly || 'none-readonly-input'"
          @input="debouncedHandleInput"
          @focusin="onFocusin"
          @focusout="onFocusout"
        />
        <vs-icon
          v-if="!readonly && !inputValue"
          icon-pack="feather"
          icon="icon-calendar"
          style="
            color: #3c3c3c;
            margin-top: 6px;
            margin-bottom: 6px;
            margin-right: 1px;
            padding-left: 6px;
            padding-right: 6px;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            font-size: 24px;
            border-left: 1px solid #cdcdcd;
            background-color: #fff;
          "
          class="cursor-pointer"
          @click="onCalendarIconClick(readonly, togglePopover)"
        ></vs-icon>
        <vs-icon
          v-if="clearable && !readonly && inputValue"
          icon-pack="feather"
          icon="icon-x"
          style="
            color: #3c3c3c;
            margin-top: 6px;
            margin-bottom: 6px;
            margin-right: 1px;
            padding-left: 6px;
            padding-right: 6px;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            font-size: 24px;
            border-left: 1px solid #cdcdcd;
            background-color: #fff;
          "
          class="cursor-pointer"
          @click="!readonly && clickXIcon()"
        ></vs-icon>
      </div>
    </template>
  </vc-date-picker>
</template>

<script>
// https://v2.vcalendar.io/
import Datepicker from "vuejs-datepicker";
import * as lang from "vuejs-datepicker/src/locale";

export default {
  name: "DateSelector",
  components: {
    Datepicker,
  },
  props: {
    value: { type: String, default: "" },
    division: { type: String, default: "-" },
    format: { type: String, default: "YYYY-MM-DD" },
    minimumView: { type: String, default: "day" },
    maximumView: { type: String, default: "year" },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
    readonly: Boolean,
    placeholder: String,
    // 디바운스 시간
    debounceMs: {
      type: Number,
      default: 300,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    value(data) {
      if (data) {
        this.date = new Date(data);
      } else {
        this.date = "";
      }
    },
  },
  data() {
    return {
      languages: lang,
      date: "",
      masks: {
        input: this.format,
        title: "YYYY년 MMMM",
      },
      debounceTimer: null,
      observer: null,
      movedPopoverEl: null,
    };
  },

  created() {
    this.date = this.value ? new Date(this.value) : "";
    this.div = this.division;
  },
  mounted() {
    this.$nextTick(() => {
      this.movePopoverToBody();
    });
  },
  beforeDestroy() {
    this.cleanupPopover();
  },
  methods: {
    onInputClick(isReadonly, showPopover) {
      if (isReadonly) return;
      try {
        showPopover({ placement: "auto-start" });
        // 팝오버 DOM이 렌더링된 직후 body로 이동 보장
        setTimeout(() => this.ensurePopoverMovedAfterOpen(), 0);
      } catch (err) {
        // no-op
      }
    },
    onCalendarIconClick(isReadonly, togglePopover) {
      if (isReadonly) return;
      try {
        togglePopover({ placement: "auto-start" });
        // 팝오버 DOM이 렌더링된 직후 body로 이동 보장
        setTimeout(() => this.ensurePopoverMovedAfterOpen(), 0);
      } catch (err) {
        // no-op
      }
    },
    updateDate(day) {
      // 이미 선택된 날짜를 다시 누르면 v-calendar 가 선택을 해제(null)한다. 이때는 누른 날짜를 그대로 유지한다
      // (같은 틱에서 되돌리면 달력이 변경을 감지하지 못해 입력칸이 비어 보이므로 다음 틱에 되돌린다)
      if (!this.date && day && day.date) {
        this.$nextTick(() => {
          this.date = day.date;
          this.$emit("input", new Date(this.date).format("yyyy-MM-dd"));
          this.$emit("change", this.date);
        });
        return;
      }
      if (!this.date) {
        return;
      }
      this.$emit("input", new Date(this.date).format("yyyy-MM-dd"));
      this.$emit("change", this.date);
    },
    clickXIcon() {
      this.date = "";
      this.$emit("input", "");
      this.$emit("change", "");
    },
    /**
     * vs-input 컴포넌트에 입력된 값을 처리합니다.
     */
    handleInput(inputValue) {
      const parsedDate = Date.parse(inputValue);
      if (!isNaN(parsedDate)) {
        let selectedDate = new Date(parsedDate);

        // maxDate가 설정되어 있고, 선택된 날짜가 maxDate보다 미래인 경우
        if (this.maxDate) {
          const maxDateTime = new Date(this.maxDate).getTime();
          const selectedDateTime = selectedDate.getTime();

          if (selectedDateTime > maxDateTime) {
            // 오늘 날짜로 강제 설정
            selectedDate = new Date(this.maxDate);
            const formattedDate = selectedDate.format("yyyy-MM-dd");
            this.date = selectedDate;
            this.$emit("input", formattedDate);
            this.$emit("change", selectedDate);

            // 사용자에게 알림
            this.$vs.notify({
              title: '날짜 제한',
              text: '오늘 이후의 날짜는 선택할 수 없습니다.',
              color: 'warning',
              position: 'top-right'
            });

            try {
              this.$refs.datePicker.move(this.date);
            } catch (err) {
              // no-op
            }
            return;
          }
        }

        this.date = selectedDate;
        this.$emit("input", inputValue);
        this.$emit("change", this.date);
      } else {
        // v-model 값은 항상 yyyy-MM-dd 문자열로 유지한다. (Date 객체가 저장되면 날짜 계산이 깨짐)
        this.$emit("input", this.date ? new Date(this.date).format("yyyy-MM-dd") : "");
        this.$emit("change", this.date);
      }

      try {
        this.$refs.datePicker.move(this.date);
      } catch (err) {
        // no-op
      }
    },
    debouncedHandleInput(inputValue) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.handleInput(inputValue);
      }, this.debounceMs);
    },
    onFocusin () {
      this.$emit("focusin");
    },
    onFocusout() {
      this.$emit("focusout");
    },

    movePopoverToBody() {
      this.$nextTick(() => {
        const rootEl = this.$refs.datePicker && this.$refs.datePicker.$el;
        if (!rootEl) {
          return;
        }
        const localPopover = rootEl.querySelector && rootEl.querySelector('.vc-popover-content-wrapper');
        if (!localPopover) {
          return;
        }
        if (!localPopover.dataset.moved) {
          document.body.appendChild(localPopover);
          localPopover.style.zIndex = '9999';
          localPopover.dataset.moved = 'true';
          this.movedPopoverEl = localPopover;
        }
      });
    },

    ensurePopoverMovedAfterOpen() {
      this.$nextTick(() => {
        const rootEl = this.$refs.datePicker && this.$refs.datePicker.$el;
        let target = null;
        if (rootEl && rootEl.querySelector) {
          target = rootEl.querySelector('.vc-popover-content-wrapper');
        }
        if (!target) {
          const candidates = Array.from(document.querySelectorAll('.vc-popover-content-wrapper'));
          target = candidates.find((el) => !el.dataset.moved);
        }
        if (target && !target.dataset.moved) {
          document.body.appendChild(target);
          target.style.zIndex = '9999';
          target.dataset.moved = 'true';
          this.movedPopoverEl = target;
        }
      });
    },

    cleanupPopover() {
      // disconnect legacy observer if any
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.movedPopoverEl && this.movedPopoverEl.parentNode === document.body) {
        this.movedPopoverEl.remove();
      }
      this.movedPopoverEl = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.right-icon {
  font-size: 24px;
  padding: 6px;
}
.none-readonly-input::v-deep {
  input {
    background: white;
  }
}

.datePicker::v-deep {
  .vc-popover-content-wrapper.is-interactive {
    z-index: 99999;
    background-color: #fff !important;
  }
}
</style>

<style>
.vc-popover-content-wrapper.is-interactive {
  z-index: 99999 !important;
  background-color: #fff !important;
}
.vc-popover-content-wrapper.is-interactive .vc-popover-caret.direction-bottom.align-center {
  background-color: #fff !important;
}
</style>
